import { FRAMED_SERVICE_SECTION_OPTIONS } from '../../capabilities/service/ServiceBlock';
import type { SkuResourceModulesState } from '../../platform/scene-layout/types';
import { buildDefaultDirectResourceModules } from '../../platform/scene-layout/utils/resolveModuleDefaults';
import type { DeploySceneConfig } from '../../platform/scenes/types';
import { VM_MODULES } from './modules';

export const VM_ALL_MODULES = VM_MODULES.map((m) => m.key);

export const VM_SKU_MODULES = ['systemSetting', 'resource', 'service', 'dataDisk', 'dns'] as const;

export type VmSkuModuleKey = (typeof VM_SKU_MODULES)[number];

/** 传给 ResourceEditor.modules（string[]，避免 readonly tuple 与 mod.key 的 includes 类型冲突） */
export const VM_SKU_MODULE_LIST: string[] = [...VM_SKU_MODULES];

export function isVmSkuModuleKey(moduleKey: string): moduleKey is VmSkuModuleKey {
  return (VM_SKU_MODULES as readonly string[]).includes(moduleKey);
}

export function filterVmSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(VM_SKU_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 云主机直建合并「资源配置」步可见模块 */
export const VM_DIRECT_MERGED_MODULES = ['baseInfo', 'network', 'systemSetting', 'resource', 'service', 'dataDisk', 'dns'] as const;

export type VmDirectMergedModuleKey = (typeof VM_DIRECT_MERGED_MODULES)[number];

export function filterVmDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(VM_DIRECT_MERGED_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 下单页模块：直建合并去掉 baseInfo */
export const VM_ORDER_MODULES = VM_DIRECT_MERGED_MODULES.filter(
  (key): key is Exclude<VmDirectMergedModuleKey, 'baseInfo'> => key !== 'baseInfo',
);

export const VM_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...VM_ALL_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  sku: {
    modules: [...VM_SKU_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  order: {
    modules: [...VM_ORDER_MODULES],
    capabilityOptions: {},
    linkage: true,
  },
};

export function resolveVmSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return VM_SCENES[scene ?? 'direct'];
}

export interface VmDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

export const VM_DIRECT_CREATE_STEPS: VmDirectCreateStep[] = [
  { key: 'resourceConfig', title: '资源配置', modules: [...VM_DIRECT_MERGED_MODULES] },
];

function hasDnsConfig(dnsConfig: unknown): boolean {
  if (!dnsConfig || typeof dnsConfig !== 'object') return false;
  const record = dnsConfig as Record<string, unknown>;
  const listKeys = ['nameservers', 'searches', 'options'] as const;
  return listKeys.some((key) => {
    const list = record[key];
    return Array.isArray(list) && list.length > 0;
  });
}

/** 编辑回显：按已有表单数据推断直建模块勾选状态 */
export function buildVmDirectResourceModulesFromForm(values: Record<string, unknown>): SkuResourceModulesState {
  const defaults = buildDefaultDirectResourceModules('VirtualMachine');
  const dataDisks = values.dataDisks;
  const network = (values.resource as Record<string, unknown> | undefined)?.network;
  const hasDataDisks = Array.isArray(dataDisks) && dataDisks.length > 0;
  const hasDns = hasDnsConfig(network && typeof network === 'object' ? (network as Record<string, unknown>).dnsConfig : undefined);
  const hasServices =
    Array.isArray((network as Record<string, unknown> | undefined)?.service) &&
    ((network as Record<string, unknown>).service as unknown[]).length > 0;

  return {
    ...defaults,
    ...(hasDataDisks ? { dataDisk: true } : {}),
    ...(hasDns ? { dns: true } : {}),
    ...(hasServices ? { service: true } : {}),
  };
}
